/*
  This singleton class is used to debounce functions based on queue and key.

  Each queue that a debounced function waiting for execution is immediately triggered before the new key debounce is set.
*/

let keyedDebounceInstance: any;

interface DebounceQueue {
  key: string;
  timerId: NodeJS.Timeout;
  delayedFunction: Function;
  retries: number;
}

interface DebounceParams {
  queue: string;
  key: string;
  delayedFunction: Function;
  delay: number;
  retry?: boolean;
  maxRetries?: number;
}

export class KeyedDebounce {
  debounceQueues = new Map<string, DebounceQueue>();

  constructor() {
    if (keyedDebounceInstance) {
      throw new Error('New instance cannot be created!!');
    }

    this.debounceQueues = new Map<string, DebounceQueue>();
    // eslint-disable-next-line
    keyedDebounceInstance = this;
  }

  debounce({
    queue,
    key,
    delayedFunction,
    delay = 1000,
    retry = false,
    maxRetries = 3,
  }: DebounceParams) {
    const debounceQueue = this.debounceQueues.get(queue);

    if (debounceQueue?.key) {
      clearTimeout(debounceQueue.timerId);
      if (debounceQueue.key !== key) {
        debounceQueue.delayedFunction();
      }
    }

    this.debounceQueues.set(queue, {
      key,
      timerId: retry
        ? setInterval(() => {
            const queueEntry = this.debounceQueues.get(queue);
            try {
              delayedFunction();
              clearInterval(queueEntry?.timerId);
              this.debounceQueues.delete(queue);
            } catch (e: any) {
              let message = e.message;
              if (queueEntry) {
                if (queueEntry.retries < (maxRetries || 3)) {
                  queueEntry.retries++;
                  message = 'retrying...';
                } else {
                  clearInterval(queueEntry.timerId);
                  this.debounceQueues.delete(queue);
                  message = 'max retries reached, aborting...';
                  // eslint-disable-next-line no-console
                  console.log(e);
                }
              }
              // eslint-disable-next-line no-console
              console.error(
                `Error in debouncing ${queue} with key ${key}, ${message}`,
              );
            }
          }, delay)
        : setTimeout(() => {
            this.debounceQueues.delete(queue);
            delayedFunction();
          }, delay),
      delayedFunction,
      retries: 0,
    });
  }
}

const keyedDebounceSingleton = Object.freeze(new KeyedDebounce());

export default keyedDebounceSingleton;
